import { useSuspenseQuery } from "@tanstack/react-query";
import type { PostHog } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { sessionQueryOptions } from "@/lib/auth-client";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

type OnboardingTelemetryContext = {
  accountId: string;
  organizationId: string;
  projectId: string;
};

function captureFirstWinOnboardingEvent(
  posthog: PostHog | null | undefined,
  context: OnboardingTelemetryContext,
  event: string,
  extraProps?: Record<string, unknown>
): void {
  if (!context.accountId) {
    return;
  }

  try {
    posthog?.capture(event, {
      account_id: context.accountId,
      org_id: context.organizationId,
      project_id: context.projectId,
      onboarding_variant: "First Win Onboarding",
      ...extraProps,
    });
  } catch {
    // Ignore analytics failures so we don't crash/block
  }
}

export function useOnboardingTelemetry() {
  const posthog = usePostHog();
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { data: session } = useSuspenseQuery(sessionQueryOptions);
  const accountId = session?.user?.accountId ?? session?.user?.id ?? "";

  const captureOnboardingEvent = useCallback(
    (event: string, extraProps?: Record<string, unknown>) => {
      captureFirstWinOnboardingEvent(
        posthog,
        { accountId, organizationId, projectId },
        event,
        extraProps
      );
    },
    [accountId, organizationId, posthog, projectId]
  );

  return { captureOnboardingEvent };
}
